import type { ChangelogEntry } from "types/changelog.types"

export const changelogEntriesEn: ChangelogEntry[] = [
  {
    release: "2026-11-02",
    changes: [
      {
        type: "new",
        title: "Tower details in the Settings tab",
        description:
          "The Settings tab of the tower page shows the tower's details: general information (name, ID, type, functionality and height), location (coordinates, altitude, orientation and deflection angle) and insulators (type and length).",
      },
      {
        type: "improved",
        title: "Reworked date picker",
        description:
          "The date picker is now easier to scan and to use. Periods are grouped into hours, days and longer, and the one you're looking at is highlighted, with its exact dates shown above the list. Periods that include a forecast keep their short name and show a tag for it. A custom period is now picked on a single calendar and only takes effect when you press Apply, so the charts don't reload while you're still choosing the dates. Once applied, the button shows those dates instead of \"Custom period\". On phones the picker opens full screen.",
      },
      {
        type: "improved",
        title: "Entity column in the events table",
        description:
          "Merged the entity type and entity columns of the events table into a single column showing the entity's icon, name and ID, like the devices and power lines tables. The CSV export now lists the entity type, name and ID in separate columns.",
      },
      {
        type: "improved",
        title: "More accurate conductor resistance for ampacity",
        description:
          "Ampacity ratings compute the conductor resistance from the conductor's stranding when its layer data is complete, and DC power lines are rated with their DC resistance. Ratings at high current on ACSR conductors with three aluminum layers can be about 1% lower; conductors without complete layer data are unchanged.",
      },
    ],
  },
  {
    release: "2026-10-26",
    changes: [
      {
        type: "new",
        title: "Manage alerts from the Alerts tab",
        description:
          "Alerts can be created and edited from a drawer in the Alerts tab of devices, power lines and towers, and enabled, disabled and deleted from each alert's menu. Disabled alerts are listed too, with a filter to find them. Alert settings are no longer in the Settings tab. Alerts show their name, type, entity and last trigger in a more compact header, and the lists let you choose how many alerts each page shows, going back to the first page when a filter changes.",
      },
      {
        type: "improved",
        title: "Power lines table reads like the devices table",
        description:
          "Merged the name and ID columns of the power lines table into one Power line column, with the power line icon, the name and the ID below it, the same way the devices table shows devices.",
      },
      {
        type: "improved",
        title: "One look for dialogs and menus",
        description:
          "Dialogs, menus, popovers and chart tooltips share one look across the platform. Every dialog has the same header and buttons, removing a user asks for confirmation like deleting a group, and the focused and selected option in a dropdown are easier to see.",
      },
      {
        type: "fixed",
        title: "Installed devices",
        description:
          'A device counts as installed only once it has its span or tower. Devices missing them are listed under Not installed, with "-" in the Installed on column, and are left out of the organization page charts.',
      },
      {
        type: "fixed",
        title: "Installation details saving",
        description:
          "Fixed the installation details card loading forever after saving. Changing tower B now clears the span so it has to be picked again, the installation date is asked for only after choosing an installation type, and a powerline installation needs both towers and the span to save.",
      },
    ],
  },
  {
    release: "2026-10-19",
    changes: [
      {
        type: "new",
        title: "Open alerts on the homepage",
        description:
          "The homepage shows the organization's open alerts under its name, with how many power lines, towers and devices they affect and a link to the Alerts overview. When there are none, it says so.",
      },
      {
        type: "new",
        title: "Alert configuration tab",
        description:
          "Each alert shows its configuration in a new tab: the thresholds with the state each one sets, the fields it watches, and its resolution, notification and note settings.",
      },
      {
        type: "improved",
        title: "Map popups as cards",
        description:
          "The map popups are redesigned as cards, with the entity's name linking to its page and the unit next to each value. A device's popup shows the span or tower it is installed on, with a link to it, and the device's version. Span and tower popups list the devices installed on them, with a link to each one.",
      },
      {
        type: "improved",
        title: "Steadier tables",
        description:
          "Reworked the tables across the platform so rows keep one height, columns keep their widths and the table keeps its size while paging, with long text shortened and shown in full on hover.",
      },
      {
        type: "fixed",
        title: "Complete audit trail CSV",
        description:
          "Fixed the audit trail CSV download to include every log in the selected period, not only the pages already viewed. The file name now includes the organization and the selected date range.",
      },
      {
        type: "fixed",
        title: "Audit trail load errors",
        description:
          "Fixed the audit trail table loading forever when the logs failed to load; it now says the data could not be loaded.",
      },
    ],
  },
  {
    release: "2026-10-05",
    changes: [
      {
        type: "new",
        title: "Span page",
        description:
          "Spans now have their own page. It opens on the long-term ampacity and conductor temperature charts, with the span's nominal ampacity and its maximum operating and emergency temperatures drawn on them, next to the last AAR and DLR ampacity and conductor temperature, and how often the span ran below nominal. A settings tab holds the span's details and its conductor. You can walk the line span by span, and reach it from the map popup, from a device, or from the devices table.",
      },
      {
        type: "new",
        title: "Conductors in power line settings",
        description:
          "The Settings tab of a power line now lists its conductors, with the type, name, nominal section and diameter of each one. Clicking a conductor opens its full datasheet: geometry, layers, thermal and mechanical properties, and electrical resistance per current type and temperature. All the values are shown in datasheet units.",
      },
      {
        type: "improved",
        title: "Span ampacity now lives on the span page",
        description:
          "The span ampacity charts are no longer on a device's Span tab. The span page charts the same data with DLR and the forecasts, and the tab links there.",
      },
      {
        type: "improved",
        title: "Line, location and devices in page headers",
        description:
          "The span, tower and device pages show the asset's power line, location and installed devices in one block under the header, with each device's reporting status.",
      },
      {
        type: "fixed",
        title: "Pole illustrations on the tilt and lean views",
        description:
          "Devices with tower health monitoring installed on a pole now show a pole instead of a lattice tower, and the tilt cards show the device on the side of the pole it is actually mounted on. The tilt diagram behind the info icon was redrawn to match.",
      },
      {
        type: "fixed",
        title: "Audit trail table loading",
        description:
          "Fixed the audit trail table staying on its loading state after accepting the same custom date range again.",
      },
    ],
  },
  {
    release: "2026-09-21",
    changes: [
      {
        type: "new",
        title: "Limiting span panel on power lines",
        description:
          "The power line Overview tab now shows how often each span was the one limiting the line's ampacity over the selected period, for both the AAR and DLR methods, in place of the plain location map. The map rings the most frequent span per method and marks the span limiting the line right now, with the time it was last reported. Under it, a per-span chart with one lane per method gives each span's share and event count on hover; clicking a span flies the map to it, and hovering or selecting a span in either view highlights it in the other. The chart shows a window of spans at a readable width, with a strip below it showing the whole line: drag the window to move it, drag either edge to resize it, or use Fit all to see every span.",
      },
      {
        type: "new",
        title: "Power line details",
        description:
          "The Settings tab of a power line now opens with its details: ID, installed at and last updated, its nominal voltage, frequency and ampacity, and its length, spans and towers.",
      },
      {
        type: "fixed",
        title: "Crosshair on phones and tablets",
        description:
          "The crosshair on line and scatter charts now works on phones and tablets as well as with a mouse.",
      },
    ],
  },
  {
    release: "2026-09-14",
    changes: [
      {
        type: "improved",
        title: "Map performance in large organizations",
        description:
          "Improved map loading in organizations with many towers or spans. The towers and spans layers are now built only when they are turned on, so maps open and pan faster. Ticking a layer on may take a moment the first time while its markers are drawn.",
      },
      {
        type: "fixed",
        title: "Towers and spans without a name",
        description:
          "Towers and spans with no name now show as Unnamed instead of null. This covers a device's installation details and its tower and span pickers, the tower dashboard map, and the alerts download filename, as power lines already did.",
      },
      {
        type: "fixed",
        title: "Time period selection on phones and tablets",
        description:
          "Selecting a time period on line and scatter charts now works on phones and tablets: place two fingers on the chart to mark the edges of the period.",
      },
    ],
  },
  {
    release: "2026-09-07",
    changes: [
      {
        type: "improved",
        title: "Overview tabs",
        description:
          "Renamed the Information tab to Overview on the device, power line, tower, and device group pages. Only the label changed; the tab shows the same content as before.",
      },
      {
        type: "fixed",
        title: "Missing values in the power lines table",
        description:
          "Fixed the power lines table showing NaN for Nominal ampacity and Nominal voltage when a power line has no value configured. Those columns and the CSV download now show a dash, as Length already did.",
      },
      {
        type: "fixed",
        title: "Search on alerts with unnamed power lines",
        description:
          "Fixed the search in the Alerts table failing when a power line has no name. Unnamed power lines now show as Unnamed there and in the alert details, as they already did on the power line and tower cards.",
      },
    ],
  },
  {
    release: "2026-08-31",
    changes: [
      {
        type: "fixed",
        title: "Dynamic line rating wind correction",
        description:
          "Fixed how the wind measured by the device is applied when computing DLR ampacity: it is now correctly treated as perpendicular to the conductor. DLR values on affected spans were previously understated and now rise to their correct level. No action is required.",
      },
    ],
  },
  {
    release: "2026-08-10",
    changes: [
      {
        type: "new",
        title: "Alerts and Settings tabs for towers",
        description:
          "Added Alerts and Settings tabs to the tower page, where alerts on tower telemetry fields can be configured and their triggered events reviewed. Available for towers with monitored health.",
      },
      {
        type: "improved",
        title: "Unified Alerts tab",
        description:
          "Merged the Alerts and Events log tabs on device, power line, and device group pages into a single Alerts tab with two sub-tabs: Configured alerts and Activity. The Activity table keeps its own date range, and the selected sub-tab and range are shareable via the URL.",
      },
      {
        type: "new",
        title: "Interactive CSV download menu",
        description:
          "Reworked the CSV download into an interactive menu. Clicking the download button next to the date picker now opens a panel where you can set the date range, choose which sources to include, and pick the individual fields per source. When the selection spans more than one file, you can download them as separate files or a single bundled .zip.",
      },
    ],
  },
  {
    release: "2026-07-27",
    changes: [
      {
        type: "new",
        title: "Segments tab for towers",
        description:
          "Added a new Segments tab to the tower page, displaying bending magnitude, broken down into its longitudinal and lateral components, and torsion, for each monitored segment of the tower. Available for towers with monitored health.",
      },
    ],
  },
  {
    release: "2026-07-21",
    changes: [
      {
        type: "new",
        title: "Tower page redesign",
        description:
          "Introduced a new tower page layout for towers with monitored health, shown when 2 or more devices are installed. It displays tower lean information with illustrative views and an overview of the data from the installed devices.",
      },
      {
        type: "new",
        title: "Ambient tab for span devices",
        description:
          "Added an Ambient tab displaying third-party weather service data for devices on spans. It appears only when the device has a geolocation set.",
      },
      {
        type: "new",
        title: "Dynamic page titles",
        description:
          "Each page now sets its own browser tab title, showing the entity name (device, tower, power line, group, organization) where applicable.",
      },
      {
        type: "new",
        title: "Short-term ampacity history chart",
        description:
          "Added a new line chart on the power line information page showing the historical short-term ampacity. It plots the 5, 10, and 15 minute forecasts over the selected date range, complementing the active short-term ampacity table.",
      },
      {
        type: "new",
        title: "Configurable wind speed cap per power line",
        description:
          "The wind-speed cap used for Ambient Adjusted Rating (AAR) and AAR/DLR forecasts can now be tuned per power line on request. Power lines without a custom value keep the previous conservative default of 0.6 m/s, so existing ratings are unchanged.",
      },
      {
        type: "improved",
        title: "Raw data in alert charts",
        description:
          "Alert charts now show raw data for periods shorter than 24 hours, and the chart is shown first in the alert details for quicker access.",
      },
      {
        type: "improved",
        title: "Chart interaction and performance",
        description:
          "Reduced the number of data points shown in aggregated periods to improve hover interaction and overall performance on both desktop and mobile.",
      },
      {
        type: "improved",
        title: "Backend security update",
        description: "Implemented security fixes in the backend.",
      },
      {
        type: "fixed",
        title: "Empty tabs in multi-tab plots",
        description:
          'Fixed multi-tab plots disappearing when switching to a tab with no data in the selected range, most noticeable after zooming. The plot now stays in place with its tabs available and shows a "no data in the selected time window" message.',
      },
      {
        type: "fixed",
        title: "Chart tooltip position on scroll",
        description:
          "Fixed the tooltip on line and scatter charts appearing in the top-left corner when a chart scrolled under a stationary cursor. The tooltip now appears in the correct position next to the cursor.",
      },
    ],
  },
  {
    release: "2026-07-06",
    changes: [
      {
        type: "improved",
        title: "Seasonal ampacity reference line",
        description:
          "The nominal ampacity reference line on the power line ampacity plot now supports seasonal ratings. When a power line defines seasonal ampacity values, the reference line becomes stepped, changing its level on the configured dates and labeling each segment with its value. The percentage below nominal ampacity stat now accounts for this too, comparing each data point against the seasonal rating in effect on its date. Power lines without seasonal values continue to display the single nominal ampacity line.",
      },
      {
        type: "new",
        title: "Span-level ampacity metrics",
        description:
          "Ampacity metrics are now tracked at the individual span level for current, forecast, and DLR calculations.",
      },
    ],
  },
  {
    release: "2026-06-29",
    changes: [
      {
        type: "improved",
        title: "Error handling for plots",
        description:
          "Improved error handling for plots. If only part of the data could be loaded, the chart displays a warning alongside the available data and a retry option. If all data failed to load, the chart displays an error message and a retry option.",
      },
      {
        type: "improved",
        title: "Map moved to device tab",
        description:
          "Moved the map to the device tab on the device page for better organization of device-related information.",
      },
      {
        type: "improved",
        title: "Short-term ampacity table view",
        description:
          "Replaced the three individual short-term ampacity stats (Next 5, 10, and 15 min) with a single table displaying timeframe, ampacity value, and measurement timestamp for each forecast interval in a more compact and readable format.",
      },
      {
        type: "new",
        title: "Percentage below nominal ampacity",
        description:
          "Added a new stat on the power line information page showing the percentage of data points below the nominal ampacity threshold, broken down by DLR and AAR. The percentage is computed over the ampacity series for the selected date range, providing a quick summary of how often each rating method operates below the nominal capacity.",
      },
      {
        type: "new",
        title: "Tilt telemetries for devices on towers",
        description:
          "For specific devices on towers, the device position section now shows new telemetries, referenced to the tower's front rather than the device's own orientation: front tilt and right tilt, tilt magnitude, and heading, along with their amplitude and drift stats.",
      },
    ],
  },
  {
    release: "2026-06-22",
    changes: [
      {
        type: "fixed",
        title: "Ghost points in scatter charts after zooming",
        description:
          "Fixed data points sometimes persisting on scatter charts after changing the date range.",
      },
      {
        type: "fixed",
        title: "Scatter chart hover activating on distant points",
        description:
          "Fixed hover highlights triggering on distant or empty areas of scatter charts. Hover now only activates when the cursor is within a short radius of the nearest data point.",
      },
    ],
  },
  {
    release: "2026-06-15",
    changes: [
      {
        type: "new",
        title: "Power line tower alerts",
        description:
          "Added alert settings management for power line towers. Users can now create, modify, and delete threshold alerts for tower telemetry fields via the tower alerts settings endpoint.",
      },
    ],
  },
  {
    release: "2026-06-10",
    changes: [
      {
        type: "improved",
        title: "Simplified reporting statuses",
        description:
          'Removed the "Stopped" reporting status. "Inactive" is now shown in orange to indicate that user action is required.',
      },
      {
        type: "improved",
        title: "Devices segmented by installation status",
        description:
          "The organization devices table is now segmented by installation status, and organization pie charts now display installed devices only.",
      },
      {
        type: "fixed",
        title: "Backend security update",
        description: "Implemented security fixes in the backend.",
      },
    ],
  },
  {
    release: "2026-06-01",
    changes: [
      {
        type: "improved",
        title: "Latest-value stats in sag and conductor tension plots",
        description:
          "Introduced single-value stats displaying the latest value for each line in conductor tension and sag plots.",
      },
      {
        type: "improved",
        title: "Explanatory diagram in conductor tension plot",
        description: "Introduced explanatory diagram for the conductor tension plot.",
      },
    ],
  },
  {
    release: "2026-05-18",
    changes: [
      {
        type: "new",
        title: "Telemetry point quality indicators",
        description:
          "In high-resolution data mode, telemetry plot data points now reflect their quality level visually: points with degraded, unreliable, or invalid quality are rendered with a colored ring indicating the severity. The plot tooltip also displays the formatted value per field at each timestamp, consistent with the quality color coding used in the summary icon.",
      },
    ],
  },
  {
    release: "2026-05-11",
    changes: [
      {
        type: "new",
        title: "Synchronized chart crosshairs",
        description:
          "Introduced synchronized crosshairs across all line and scatter charts. Hovering over one chart now mirrors the cursor position across the entire dashboard, allowing for easier data comparison.",
      },
      {
        type: "new",
        title: '"Installed on" field in device summary',
        description:
          'Added an "Installed on" field to the device summary header, displaying the entity the device is installed on alongside its corresponding icon and a direct link to that entity\'s page.',
      },
    ],
  },
  {
    release: "2026-05-04",
    changes: [
      {
        type: "new",
        title: "Data quality summary in telemetry plots",
        description:
          "Introduced data quality summary visualization for telemetry plots. A mini pie chart icon displays the percentage breakdown of data quality levels (healthy, degraded, unreliable, invalid) for the selected time period, helping users assess measurement reliability at a glance. Clicking the icon opens a detailed dialog with a full pie chart, per-level percentages and descriptions, and a reference to the API endpoints for retrieving quality metadata.",
      },
      {
        type: "new",
        title: "High-resolution data for short time ranges",
        description:
          "When the selected date range is 24 hours or less, telemetry plots automatically switch to high-resolution mode, showing exact data points without aggregation or averaging. A visual indicator in the plot header reflects the current data mode.",
      },
    ],
  },
  {
    release: "2026-04-27",
    changes: [
      {
        type: "new",
        title: "CSV download for Power Line, Tower, and Device pages",
        description:
          "Added a CSV download button to the Power Line, Tower, and Device pages. When the download button next to the date picker is clicked, all telemetries displayed on that dashboard are downloaded. If there are telemetries associated with related entities, a separate file is generated for each. This functionality is currently available to editors only.",
      },
      {
        type: "improved",
        title: "Updated telemetry API documentation",
        description:
          "Improved the telemetry API documentation to list all dashboard-visible telemetries, making it easier to identify which fields are available for querying.",
      },
    ],
  },
  {
    release: "2026-04-13",
    changes: [
      {
        type: "improved",
        title: "Pole tower illustrations for v4.0 devices",
        description:
          "Added new illustrations to the position plot for v4.0 devices installed on pole-type towers, improving the visual representation of alpha and beta angles in these configurations.",
      },
      {
        type: "new",
        title: "Quality metadata for telemetries",
        description:
          'Added quality metadata for selected telemetries. Use the /telemetry query with include_field_metadata=true to get associated metadata (quality_index, quality, reason), or use only_quality_summary=true for a summary of quality metadata over the selected period. Associated metadata includes three new telemetries per telemetry: .metadata.quality (either "healthy", "degraded", "unreliable", or "invalid", indicating how good the data is), .metadata.reason (when the quality isn\'t "healthy", lists the reason why) and .metadata.quality_index (an integer mapping quality from better to worse, where 1 is "healthy"). For instance, querying field=ampacity.aar&field=ampacity.aar.metadata.quality_index will yield both the AAR ampacity and the quality index of that telemetry.',
      },
    ],
  },
  {
    release: "2026-04-06",
    changes: [
      {
        type: "improved",
        title: "Tab navigation icons",
        description:
          "Improved tab navigation by adding icons to tab labels across all pages and enhancing interactivity with a subtle hover background effect and rounded corners.",
      },
    ],
  },
  {
    release: "2026-03-30",
    changes: [
      {
        type: "new",
        title: "Tower device illustrations",
        description:
          "Introduced illustrations for new tower devices, showing position angles relative to the installation section.",
      },
    ],
  },
  {
    release: "2026-03-23",
    changes: [
      {
        type: "new",
        title: "Ambient tab for tower devices",
        description:
          "Added a conditional Ambient tab for devices installed on a tower that have a weather station attached. This tab displays the information reported by the weather station.",
      },
    ],
  },
  {
    release: "2026-03-16",
    changes: [
      {
        type: "new",
        title: "Tower and Device tabs",
        description:
          "Split the tower device Information tab into separate Tower and Device tabs for clearer organization of device information.",
      },
      {
        type: "new",
        title: "Nominal ampacity reference line",
        description:
          "Added a nominal ampacity reference line to the power line page ampacity plot for easier comparison of ampacity values against the nominal threshold.",
      },
      {
        type: "improved",
        title: "Trigger window date range",
        description:
          "Renamed the 'last trigger' date reference to 'trigger window' for clarity. The trigger window period now starts one day before the alert was triggered, instead of one week, providing a more focused view of the event context.",
      },
      {
        type: "fixed",
        title: "Plot date range bounds",
        description:
          "Fixed line and scatter plots to always display the full selected date range on the x-axis. Previously, the axis would start at the first available data point rather than the requested start date, causing the visible range to appear shorter than expected.",
      },
    ],
  },
  {
    release: "2026-02-23",
    changes: [
      {
        type: "new",
        title: "Critical span display",
        description:
          "Introduced display for the last critical spans, both for ampacity AAR and ampacity DLR in the power line map on the power line page.",
      },
    ],
  },
  {
    release: "2026-02-18",
    changes: [
      {
        type: "new",
        title: "Span telemetry endpoint",
        description:
          "Introduced a new endpoint to query span related data: '/powerlines_spans/int:span_id/telemetry'.",
      },
      {
        type: "new",
        title: "Span ampacity plots on device page",
        description:
          "Introduced 'Span Ampacity (AAR)' and 'Last Span Ampacity (AAR)' plots to the device page's Span tab, displaying AAR data for the span the device is installed on.",
      },
      {
        type: "improved",
        title: "Map layers control enhancement",
        description:
          "Enhanced the map layers control colors for better visual consistency. Additionally, the layers control now appears expanded by default on large maps for easier access to layer visibility options.",
      },
      {
        type: "improved",
        title: "Ampacity plot descriptions",
        description:
          "Updated AAR and DLR descriptions on the power line ampacity plot for improved clarity.",
      },
    ],
  },
  {
    release: "2026-02-09",
    changes: [
      {
        type: "new",
        title: "Weather station single stats",
        description:
          "Introduced single stats displaying the last value for each plot in weather station devices to ensure consistency and improve usability.",
      },
      {
        type: "new",
        title: "Weather service single stats",
        description:
          "Introduced single stats displaying the latest value for each weather service telemetry plot on the Tower page to ensure consistency and improve usability.",
      },
      {
        type: "new",
        title: "Extended forecast period",
        description:
          "Extended the custom date range selector on the powerline page to allow selection up to 14 days in the future, enabling visualization of a longer forecast period.",
      },
      {
        type: "new",
        title: "Separate AAR and DLR ampacity forecast fields",
        description:
          "Ampacity forecast fields now display separate values for AAR (Ambient Adjusted Rating) and DLR (Dynamic Line Rating) to provide more detailed information.",
      },
      {
        type: "new",
        title: "Ampacity forecast visualization enhancement",
        description:
          "Added a shaded area between the AAR and DLR forecast lines in the ampacity plot to better visualize the operating range.",
      },
      {
        type: "improved",
        title: "Conservative AAR calculations",
        description:
          "AAR is now calculated using at most 0.6 m/s or a lower value provided from weather service. This results in a more conservative ampacity following industry practice and IEEE and CIGRE suggestions.",
      },
    ],
  },
  {
    release: "2026-02-02",
    changes: [
      {
        type: "new",
        title: "Device inclination plot on tower page",
        description:
          "Introduced a plot showing the average inclination data for all the devices on a tower to improve user experience.",
      },
    ],
  },
  {
    release: "2026-01-26",
    changes: [
      {
        type: "new",
        title: "Map interaction overlay",
        description:
          "Added an interaction overlay to maps to prevent accidental zooming while scrolling the board. Users can now click the map to enable full interaction, ensuring a smoother navigation experience across the page.",
      },
      {
        type: "new",
        title: "Wind direction plot reference",
        description:
          "Added an explanation on how to interpret weather station wind direction values to the plot tooltip for better understanding.",
      },
      {
        type: "new",
        title: "Plot zoom functionality",
        description:
          "Introduced zoom functionality for plots allowing users to select specific time periods by dragging on the chart. While in zoom mode, the selected period is preserved when navigating between tabs within the same page and shown in the date picker with a '(zoomed in)' indicator. Users can exit zoom mode via a dedicated button or by selecting a new date range, automatically returning to live data updates. Zoom mode automatically ends when navigating away from the page. Note: Custom date ranges are directly replaced with a new custom range instead of entering zoom mode.",
      },
      {
        type: "improved",
        title: "Date picker loading indicator",
        description:
          "Improved the accuracy of the date picker loading spinner. The spinner now appears only when a plot on the current page is loading, providing clearer feedback about data loading status.",
      },
    ],
  },
  {
    release: "2026-01-19",
    changes: [
      {
        type: "new",
        title: "Weather station dashboard tabs",
        description:
          "Weather station type devices' board is now split into two separate ambient and device tabs for better organization and clarity.",
      },
    ],
  },
  {
    release: "2026-01-12",
    changes: [
      {
        type: "new",
        title: "Wind gusts plot",
        description:
          "Introduced a new weather service plot showing the wind gusts on the tower page.",
      },
      {
        type: "improved",
        title: "Tower page auto-refresh",
        description:
          "Updated the tower page to refresh every 5 minutes, consistent with other entity pages.",
      },
    ],
  },
  {
    release: "2025-12-18",
    changes: [
      {
        type: "new",
        title: "Ice accretion detection plot",
        description:
          "Introduced a new plot showing potential ice accretion. The y-axis displays icing types (Glaze, Glaze and hard rime, Hard rime, Wet snow and glaze, Wet snow, and No ice). Horizontal bars indicate the duration of each detected icing type, and the colors and the tooltip inform its severity level (0 to 5).",
      },
      {
        type: "new",
        title: "Ice accretion single stat",
        description:
          "Added a single stat showing the last ice accretion type detected of the selected time period.",
      },
      {
        type: "improved",
        title: "Span limiting temperature display",
        description:
          "Added the span's limiting temperature to the span map tooltip, when present. Spans are now visible by default on the power line dashboard for better visibility.",
      },
    ],
  },
  {
    release: "2025-12-09",
    changes: [
      {
        type: "improved",
        title: "Motion intensity plot enhancements",
        description:
          "Enhanced the motion intensity plot with minor term adjustments and added the appropriate unit.",
      },
      {
        type: "new",
        title: "Cumulative fatigue single stat",
        description:
          "Added a single stat displaying the last cumulative fatigue value for the selected time period in the span tab on the device page.",
      },
      {
        type: "improved",
        title: "Map interaction hover effects",
        description:
          "Introduced hover effects for map elements, including line widening for power lines and spans, reduced opacity for non-hovered elements to make start and end points easier to distinguish, and a scale-up effect for devices and towers.",
      },
    ],
  },
  {
    release: "2025-12-01",
    changes: [
      {
        type: "new",
        title: "Cumulative fatigue plot",
        description:
          "Replaced the daily fatigue plot with the cumulative fatigue plot to provide more accurate and useful data.",
      },
      {
        type: "new",
        title: "Forecast section shading",
        description:
          "Added a shaded background for plots that include forecasts to improve visual distinction between actual and forecasted data.",
      },
      {
        type: "improved",
        title: "Installation details display",
        description:
          "Enhanced installation details to display entity names next to their corresponding IDs, making the information clearer and easier to identify.",
      },
      {
        type: "improved",
        title: "Data grouping notes specificity",
        description:
          "Refined the help notes for each plot regarding data grouping used, providing more specific information for each grouping function.",
      },
      {
        type: "improved",
        title: "Motion alert autoresolve description",
        description:
          "Added a detailed explanation of the motion alert autoresolve functionality in the alert setting creation form.",
      },
      {
        type: "improved",
        title: "Expired token error message",
        description:
          "Improved error handling for expired authentication tokens, now returning a clear 'Token has expired' message.",
      },
    ],
  },
  {
    release: "2025-11-25",
    changes: [
      {
        type: "new",
        title: "Short-term ampacity timestamp display",
        description:
          "Added timestamp display for short-term ampacity data to show when measurements were taken.",
      },
      {
        type: "new",
        title: "Original timestamp in telemetry plots",
        description:
          "Enhanced plots to use the actual measured data timestamp whenever possible, improving accuracy.",
      },
      {
        type: "fixed",
        title: "CSV export with special characters",
        description:
          "Fixed CSV downloads to properly handle special characters in both table and plot data exports.",
      },
      {
        type: "fixed",
        title: "Alert state updates",
        description:
          "Alert actions now only work on previously triggered alerts, preventing unintended state changes.",
      },
    ],
  },
  {
    release: "2025-11-17",
    changes: [
      {
        type: "new",
        title: "Cumulative fatigue telemetry",
        description:
          "Added interpolated cumulative fatigue telemetry for days without daily fatigue values, improving continuity in fatigue tracking.",
      },
      {
        type: "fixed",
        title: "Short-term ampacity data query",
        description:
          "Fixed the query parameter for short-term ampacity data retrieval to ensure proper data aggregation.",
      },
    ],
  },
  {
    release: "2025-11-10",
    changes: [
      {
        type: "new",
        title: "Changelog feature",
        description:
          "Introduced a new changelog feature with a popover showing the latest release changes, a dedicated changelog page displaying full history, and a 'New' badge that appears for two days after each release.",
      },
      {
        type: "new",
        title: "Weather data visualization",
        description:
          "Added weather service plots to the tower page, including wind speed, wind direction, temperature, relative humidity, and precipitation charts for enhanced environmental monitoring.",
      },
      {
        type: "new",
        title: "Short-term ampacity visualization",
        description:
          "Added support for displaying short-term ampacity data to help operate power lines in extreme conditions.",
      },
      {
        type: "improved",
        title: "Language selection moved to profile menu",
        description:
          "Relocated the language selector to the profile menu for a cleaner interface and improved user experience.",
      },
      {
        type: "fixed",
        title: "Sag tooltip display",
        description:
          "Removed X and Y coordinate values from the sag tooltip for more accurate data presentation.",
      },
    ],
  },
  {
    release: "2025-11-03",
    changes: [
      {
        type: "new",
        title: "Short-term ampacity calculations",
        description:
          "Added support for short-term and emergency ampacity telemetries, enabling better prediction of power line capacity under varying conditions.",
      },
      {
        type: "new",
        title: "Weather service API endpoint",
        description:
          "Introduced a new /weather_service endpoint that allows users to access third party weather data for improved environmental monitoring.",
      },
    ],
  },
  {
    release: "2025-10-27",
    changes: [
      {
        type: "new",
        title: "Powerline details",
        description:
          "Added details to the powerline page including ID, nominal voltage, nominal ampacity, and length for better overview.",
      },
      {
        type: "new",
        title: "Unknown reporting status",
        description: "Added a new reporting status to improve monitoring.",
      },
    ],
  },
  {
    release: "2025-10-20",
    changes: [
      {
        type: "new",
        title: "Tower details",
        description:
          "Added details to the tower page including ID, type, and height for better overview.",
      },
      {
        type: "fixed",
        title: "Device illustrations",
        description:
          "Fixed the button placement and element layering in the illustrations of devices installed on towers for better clarity.",
      },
    ],
  },
]
